import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import Logo from '@/assets/img/logo/logo.png';
import greenLogo from '@/assets/img/logo/greenLogo.png';
import * as S from '@/components/Header/style';
import { menuList } from '@/constants/menu';
import LinkIcon from '../Icon/LinkIcon';
import useIsMobile from '@/hooks/useIsMobile';
import MobileMenu from './MobileMenu/MobileMenu';
import UserIcon from '../Icon/UserIcon';
import MypageMobileMenu from './MypageMobileMenu/MypageMobileMenu';
import MenuIcon from '../Icon/MenuIcon';
import { useModalStore } from '@/store/modalStore';
import { useSignUpStore } from '../SignUp/SignUpContext/SignUpContext';
import { useTokenStore } from '@/store/tokenStore';
import { useScroll } from '@/hooks/useScroll';

const Header = () => {
	const isScrolled = useScroll();
	const isMobile = useIsMobile(1180);
	const navigate = useNavigate();
	const { isLogin } = useTokenStore();
	const { pathname } = useLocation();
	const [isOpen, setIsOpen] = useState(false);
	const [isOpenMypage, setIsOpenMypage] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const { showModal } = useModalStore();
	const { setStep } = useSignUpStore();
	// Helper functions
	const isAuthPath =
		pathname.split('/')[1].toLowerCase() === 'auth' ||
		pathname.split('/')[1].toLowerCase() === 'mypage' ||
		pathname.split('/')[1].toLowerCase() === 'conference';
	const iconColor = isAuthPath ? '#01337D' : isHovered && !isMobile ? '#01337D' : '#fff';
	const defaultColor = isAuthPath ? '#212529' : isHovered && !isMobile ? '#212529' : isScrolled ? '#212529' : '#fff';
	const backgroundColor = isAuthPath ? '#fff' : isHovered && !isMobile ? '#fff' : isScrolled ? '#fff' : ' rgba(23, 23, 23, 0.45)';

	useEffect(() => {
		if (isOpen) {
			document.body.classList.remove('hidden');
			setIsOpen(false);
		}
		if (isOpenMypage) {
			document.body.classList.remove('hidden');
			setIsOpenMypage(false);
		}
	}, [isMobile]);

	const onClickOpen = () => {
		document.body.classList.add('hidden');
		setIsOpen(true);
	};

	const onClickOpenMypage = () => {
		document.body.classList.add('hidden');
		setIsOpenMypage(true);
	};

	const onClickSubMenu = (path: string, title: string) => {
		if (title === '회원가입') {
			if (isLogin()) {
				showModal('이미 노화학회 회원이십니다.', 'confirm');
				return;
			} else {
				navigate('/auth/signup');
				return;
			}
		}
		if (title === '연회비 납부') {
			if (!isLogin()) {
				showModal('로그인이 필요합니다.', 'login', '로그인 하러가기', () => navigate('/auth/login'));
				return;
			}
		}
		navigate(path ?? '', { state: { menu: title } });
	};
	const getLogoSrc = () => {
		if (isAuthPath || (isHovered && !isMobile) || isScrolled) {
			return greenLogo;
		}
		return Logo;
	};

	const handleMenuClick = (path: string) => {
		if (!path) return;
		if (path === 'link') {
			window.open('https://www.e-agmr.org/');
		} else {
			navigate(path);
		}
	};

	const renderMenuItems = () =>
		menuList.map((menu, index) => (
			<div key={index}>
				<S.MenuItem isHover={isHovered} defaultColor={defaultColor} onClick={() => handleMenuClick(menu.mainPath ?? '')}>
					{menu.mainPath === 'link' ? (
						<>
							{menu.mainMenu}
							<LinkIcon iconColor={defaultColor} />
						</>
					) : (
						menu.mainMenu
					)}
				</S.MenuItem>

				{/* 서브 메뉴는 따로 묶어 */}
				{isHovered && menu.subMenu && (
					<S.SubMenuList>
						{menu.subMenu.map((subMenu, subIndex) => (
							<S.MenuItem className="sub" key={subIndex} defaultColor={defaultColor} onClick={() => onClickSubMenu(subMenu.path ?? '', subMenu.title ?? '')}>
								{subMenu.title}
							</S.MenuItem>
						))}
					</S.SubMenuList>
				)}
			</div>
		));

	const renderAuthItems = () =>
		!isMobile ? (
			<>
				{isLogin() ? (
					<S.UserButton type="button" onClick={() => navigate('/mypage')}>
						<UserIcon width="20" height="20" />
						MY
					</S.UserButton>
				) : (
					<>
						<S.AuthButton defaultColor={defaultColor} type="button" onClick={() => onClickSubMenu('/auth/login', '로그인')}>
							로그인
						</S.AuthButton>
						<S.AuthButton defaultColor={defaultColor} type="button" onClick={() => onClickSubMenu('/auth/signup', '회원가입')}>
							회원가입
						</S.AuthButton>
					</>
				)}
			</>
		) : (
			<>
				{isLogin() ? (
					<S.UserButton type="button" onClick={onClickOpenMypage}>
						<UserIcon width="20" height="20" />
						MY
					</S.UserButton>
				) : (
					<S.AuthButton defaultColor={defaultColor} type="button" onClick={() => onClickSubMenu('/auth/login', '로그인')}>
						로그인
					</S.AuthButton>
				)}

				<MenuIcon iconColor={defaultColor} onClick={() => setIsOpen(!isOpen)} />
			</>
		);

	return (
		<>
			<S.HeaderContainer
				id="header"
				isHover={isHovered}
				isAuthPath={isAuthPath}
				defaultColor={backgroundColor}
				isScrolled={isScrolled}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				<S.Nav isHover={isHovered}>
					{/* Logo and Menu Container */}
					<S.LogoBox className={isHovered ? 'hover' : ''}>
						<Link to="/">
							<S.ImgBox>
								<img src={getLogoSrc()} alt="Logo" />
							</S.ImgBox>
						</Link>
					</S.LogoBox>
					<S.MenuList isHover={isHovered}>{renderMenuItems()}</S.MenuList>
					<S.AuthBox isLogin={isLogin()}>{renderAuthItems()}</S.AuthBox>
				</S.Nav>
			</S.HeaderContainer>

			{/* Modal Menus */}
			<MobileMenu isOpen={isOpen} onClickIsOpen={(bol) => setIsOpen(bol)} />
			<MypageMobileMenu isOpen={isOpenMypage} onClickIsOpen={(bol) => setIsOpenMypage(bol)} />
		</>
	);
};

export default Header;
